import { useCallback, useEffect, useState } from 'react';
import { useSaveStaffingRuleChangeMutation } from '../../../../services/LaborManagementService';
import type { StaffingRuleChangeRequest } from '../../../../services/LaborManagementService/requests/laborManagementRequests';
import type { DepartmentRuleView, StaffingRuleConflictView, StaffingRuleView } from '../../../../services/LaborManagementService/types/laborManagementTypes';

type ScopedChange = Pick<
  StaffingRuleChangeRequest,
  'rule' | 'defaultOperatingDayLengthHours' | 'defaultTraysPerRoom'
>;

// A refusal arrives as an RTK Query error carrying the conflict body, not as a thrown Error.
const asConflict = (error: unknown): StaffingRuleConflictView | null => {
  const rejection = error as { status?: unknown; data?: StaffingRuleConflictView } | undefined;
  return rejection?.status === 409 && rejection.data ? rejection.data : null;
};

// A refusal already has its own warning on the page, so it is not a failure to report twice. Either
// way the error goes on to the facility field, which puts the stored value back.
const rethrowSettingsFailure = (error: unknown): never => {
  if (!asConflict(error)) console.error('Failed to save facility settings:', error);
  throw error;
};

/**
 * Sends a staffing-rule save that carries only what was edited, and owns the concurrency token.
 *
 * The token seeds from the loaded view and advances on each saved response. Reading it from the
 * query view alone is not enough: between a successful save and the moment the invalidated refetch
 * lands, that view still holds the pre-save token, so a second save in the same sitting would be
 * refused against the administrator's own earlier write.
 *
 * A stored view with no token, and one whose token is null, seed the same absent field: the request
 * then omits `expectedVersion` rather than sending it as null, so both shapes reach the wire alike.
 *
 * Neither the grid nor the facility panel learns that a token exists.
 */
export const useScopedRuleSave = (staffingRule: StaffingRuleView | null) => {
  const [saveChange, { isLoading: isSaving }] = useSaveStaffingRuleChangeMutation();
  const loadedVersion = staffingRule?.version;
  const [expectedVersion, setExpectedVersion] = useState<StaffingRuleView['version']>();
  const [conflict, setConflict] = useState<StaffingRuleConflictView | null>(null);

  useEffect(() => {
    setExpectedVersion(loadedVersion ?? undefined);
  }, [loadedVersion]);

  // A refusal belongs to one facility's rule document. Moving to another abandons it: the page stays
  // mounted across a center change, so a kept conflict would warn over the new facility's values.
  const loadedId = staffingRule?.id;
  useEffect(() => {
    setConflict(null);
  }, [loadedId]);

  // The refusal is recorded here and rethrown: the grid and the panel report a failed save from a
  // rejected promise, so swallowing it would tell the administrator the save had succeeded.
  const save = useCallback(async (change: ScopedChange) => {
    if (!staffingRule) return;
    try {
      const saved = await saveChange({ id: staffingRule.id, ...change, expectedVersion }).unwrap();
      setExpectedVersion(saved.version);
      setConflict(null);
    } catch (error) {
      const refusal = asConflict(error);
      if (refusal) setConflict(refusal);
      throw error;
    }
  }, [saveChange, staffingRule, expectedVersion]);

  const saveRule = useCallback((rule: DepartmentRuleView) => save({ rule }), [save]);
  // Both saves rethrow. The grid reports a failed rule save; the facility field puts the stored value
  // back on a failed settings save, and it catches the rejection, so nothing reaches the browser
  // as an uncaught error.
  const saveSettings = useCallback(
    (settings: Omit<ScopedChange, 'rule'>) => save(settings).catch(rethrowSettingsFailure), [save],
  );
  const clearConflict = useCallback(() => setConflict(null), []);

  return { saveRule, saveSettings, isSaving, conflict, clearConflict };
};
